import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftRight, CalendarDays, CalendarRange, Check, Circle, Clock, Columns2, Copy, Eye, EyeOff, Folder, Hand, LayoutTemplate, Loader2, Pause, Pencil, Play, Search, Settings2, Sparkles, Sunrise, Trash2, X } from 'lucide-react';
import { C } from '../theme.js';
import { Alert, Button, Code, EnvToggle, Eyebrow, FieldLabel, IconButton, Logo, Pill, Select, StatusChip, TextInput } from '../components/Primitives.jsx';
import { PHASE2_LABEL, PHASE2_TOOLTIP, pipelineOrder, resolveFilenamePattern } from '../data.js';
import { FolderPickerModal } from '../preview/Preview.jsx';

/* eslint-disable */
// ─── Projects screen + Run dialog ─────────────────────────────────────

// The Projects page's dashboard banner (title, description, stats) is hidden
// for now; flip this to bring it back. Nothing else depends on it.
const SHOW_PROJECTS_DASHBOARD = false;
// Pipeline status (Draft / Active / Paused) only reflects scheduling, which
// isn't built yet (Phase 2), so its badge, filter and "Active" count are
// hidden for now; flip this to bring them back.
const SHOW_PIPELINE_STATUS = false;

// "3 min ago" / "2 h ago" / "4 d ago" / a date, for a run's start time
// (ISO text from the server). Null when there has been no run.
function runAgo(iso) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return null;
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return new Date(t).toLocaleDateString();
}

// Last-run status, or a plain "Not run yet" (the chip's own fallback reads
// "Draft", which isn't a run status).
function LastRunChip({ status }) {
  if (!status) return <span style={{ color: C.fg5, fontSize: '.76rem' }}>Not run yet</span>;
  return <StatusChip status={status} />;
}

export function ProjectsScreen({ projects, orgName, onOpen, onNew, onImport, onRename, onDelete }) {
  const [hoverId, setHoverId] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const filtered = search
    ? projects.filter(p => p.name.toLowerCase().includes(search.toLowerCase())
        || p.description.toLowerCase().includes(search.toLowerCase()))
    : projects;

  // Aggregate stats
  const totalRows = projects.reduce((a, p) => a + (p.rowsProcessed || 0), 0);
  const totalPipes = projects.reduce((a, p) => a + (p.pipelines?.length || 0), 0);
  const errored = projects.filter(p => p.lastRunStatus === 'error').length;

  return (
    <div style={{ padding: '28px clamp(14px, 3vw, 36px)', maxWidth: 1280, margin: '0 auto', width: '100%', boxSizing: 'border-box', minWidth: 0 }} data-screen-label="01 Projects">
      {/* Hero banner */}
      {SHOW_PROJECTS_DASHBOARD && <div style={{
        background: 'linear-gradient(135deg, var(--banner-1) 0%, var(--banner-2) 50%, var(--banner-3) 100%)',
        border: `1px solid ${C.borderDashed}`,
        borderRadius: 12,
        padding: '28px clamp(16px, 3vw, 36px)',
        marginBottom: 28,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at top right, var(--banner-glow), transparent 60%)', pointerEvents:'none' }} />
        <Eyebrow style={{ color: C.accentSoft, position: 'relative' }}>Organization · {orgName || 'acme-data-eng'}</Eyebrow>
        <h1 style={{
          color: C.fg1, fontSize: '2rem', fontWeight: 700, margin: '6px 0 4px',
          letterSpacing: '-.01em', position: 'relative',
        }}>Projects</h1>
        <p style={{ color: C.fg4, fontSize: '.95rem', margin: 0, position: 'relative' }}>
          Each project holds pipelines that ingest files, run cleansing steps, and write cleaned outputs.
        </p>

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '12px 28px', marginTop: 18, position: 'relative',
        }}>
          {[
            { label: 'Projects',            value: projects.length, tone: 'neutral' },
            { label: 'Pipelines',           value: totalPipes,      tone: 'neutral' },
            { label: 'Rows last 7 d',       value: totalRows.toLocaleString(), tone: 'neutral' },
            { label: 'Pipelines erroring',  value: errored,         tone: errored > 0 ? 'warn' : 'neutral' },
          ].map(s => (
            <div key={s.label}>
              <Eyebrow>{s.label}</Eyebrow>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: '1.6rem', fontWeight: 600,
                color: s.tone === 'warn' ? C.warn : C.fg1, marginTop: 4,
              }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>}

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 18 }}>
        <div style={{ position: 'relative', flex: '1 1 160px', maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.fg5, fontSize: '.85rem' }}>⌕</span>
          <TextInput value={search} onChange={setSearch} placeholder="Filter projects…" style={{ paddingLeft: 30 }} />
        </div>
        <div style={{ flex: 1 }} />
        <Button kind="ghost" size="md" icon="⤓" onClick={onImport}>Import</Button>
        <Button kind="primary" size="md" icon="＋" onClick={onNew}>New project</Button>
      </div>

      {/* Project cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 14 }}>
        {filtered.map(p => (
          <ProjectCard
            key={p.id}
            project={p}
            hover={hoverId === p.id}
            onHover={(v) => setHoverId(v ? p.id : null)}
            onOpen={() => onOpen(p)}
            onRename={() => onRename?.(p)}
            onDelete={() => onDelete?.(p)}
          />
        ))}

        {/* "New project" card */}
        <div
          onClick={onNew}
          style={{
            background: 'transparent',
            border: `2px dashed ${C.borderDashed}`,
            borderRadius: 10,
            padding: '18px 20px',
            cursor: 'pointer',
            minHeight: 156,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: C.fg4, transition: 'border-color .2s, color .2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accentBright; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.borderDashed; e.currentTarget.style.color = C.fg4; }}
        >
          <div style={{ fontSize: 26, marginBottom: 4 }}>＋</div>
          <div style={{ fontWeight: 600, fontSize: '.88rem' }}>New project</div>
          <div style={{ fontSize: '.74rem', marginTop: 4 }}>Start from an empty pipeline or upload a file</div>
        </div>
      </div>
    </div>
  );
}

// The menu is portaled to document.body (unlike PipelineRow's identical
// menu, which doesn't need to be): this card's hover lift uses `transform`,
// and a `position: fixed` descendant of a transformed ancestor is
// positioned relative to THAT ancestor, not the viewport — so the
// getBoundingClientRect()-based top/right below would land in the wrong
// place instead of where the trigger button actually is. The hover lift is
// pre-existing, deliberate UI and is left untouched; the portal is what
// actually fixes the menu's positioning.
function ProjectCard({ project: p, hover, onHover, onOpen, onRename, onDelete }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ top: 0, right: 0 });
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const openMenu = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      setMenuPos({
        top: Math.round(r.bottom + 6),
        right: Math.round(window.innerWidth - r.right),
      });
    }
    setMenuOpen(true);
  };
  // Same viewport-anchored, flip-if-near-bottom menu as the pipeline row's
  // own ⋯ menu (PipelineRow) — project cards sit in a grid that can scroll,
  // so a fixed-position dropdown is more robust than an absolutely-
  // positioned one clipped to the card.
  React.useLayoutEffect(() => {
    if (!menuOpen || !menuRef.current || !triggerRef.current) return;
    const h = menuRef.current.offsetHeight;
    const r = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    let top = Math.round(r.bottom + 6);
    if (top + h > vh - 8) top = Math.round(r.top - h - 6);
    top = Math.max(8, Math.min(top, vh - h - 8));
    setMenuPos(pos => (pos.top === top ? pos : { ...pos, top }));
  }, [menuOpen]);
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
        && !triggerRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onScrollOrResize = () => setMenuOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [menuOpen]);

  return (
    <div
      data-screen-label={`Project · ${p.name}`}
      onClick={onOpen}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      style={{
        background: C.surface,
        border: `1px solid ${hover ? C.accentMuted : C.border}`,
        borderRadius: 10,
        padding: '18px 20px',
        cursor: 'pointer',
        transition: 'border-color .15s, transform .15s',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10,
          background: C.input, border: `1px solid ${C.accentMuted}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, flexShrink: 0,
        }}>{p.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: C.fg1, fontWeight: 700, fontSize: '.95rem', marginBottom: 2 }}>{p.name}</div>
          <div style={{
            color: C.fg4, fontSize: '.78rem', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{p.description}</div>
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            ref={triggerRef}
            onClick={() => menuOpen ? setMenuOpen(false) : openMenu()}
            title="Project actions"
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: menuOpen ? C.input : 'transparent',
              color: C.fg4, border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: '1.05rem', lineHeight: 1,
              padding: 0,
            }}
          >⋯</button>
          {menuOpen && createPortal(
            <div ref={menuRef} style={{
              position: 'fixed', top: menuPos.top, right: menuPos.right,
              background: C.app, border: `1px solid ${C.borderStrong}`,
              borderRadius: 8, padding: 4, zIndex: 60, minWidth: 180,
              boxShadow: '0 12px 32px rgba(0,0,0,.5)',
            }}>
              <MenuItem icon={<Play size={14} />} onClick={() => { setMenuOpen(false); onOpen(); }}>Open</MenuItem>
              <MenuItem icon={<Pencil size={14} />} onClick={() => { setMenuOpen(false); onRename(); }}>Edit name & description…</MenuItem>
              <div style={{ height: 1, background: C.border, margin: '4px 0' }} />
              <MenuItem icon={<Trash2 size={14} />} danger onClick={() => { setMenuOpen(false); onDelete(); }}>Delete project</MenuItem>
            </div>,
            document.body,
          )}
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginTop: 14,
        paddingTop: 12, borderTop: `1px solid ${C.border}`,
      }}>
        <Pill tone="opt" size="sm">{p.pipelines.length} pipelines</Pill>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          color: C.fg5, fontSize: '.74rem', whiteSpace: 'nowrap',
        }}>
          {(p.rowsProcessed || 0).toLocaleString()} rows
        </span>
        <div style={{ flex: 1 }} />
        <LastRunChip status={p.lastRunStatus} />
      </div>
      <div style={{
        color: C.fg5, fontSize: '.72rem', marginTop: 6,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        Last run · {runAgo(p.lastRunAt) || '—'}
      </div>
    </div>
  );
}

// ── Run pipeline modal ─────────────────────────────────────────────────
export function RunPipelineModal({ open, onClose, pipeline, onRun, onViewDetails, lastRunId }) {
  const [folder, setFolder] = React.useState(pipeline.output.folder);
  const [filename, setFilename] = React.useState(pipeline.output.filename);
  const [format, setFormat] = React.useState(pipeline.output.format || 'csv');
  const [openAfter, setOpenAfter] = React.useState(true);
  const [phase, setPhase] = React.useState('idle'); // idle | running | done | failed
  const [result, setResult] = React.useState(null); // the Run the server returned
  const [progress, setProgress] = React.useState(0);
  const [currentStepIdx, setCurrentStepIdx] = React.useState(0);
  const [folderPickerOpen, setFolderPickerOpen] = React.useState(false);
  const ordered = pipelineOrder(pipeline.steps);

  // Resolve a live filename preview using the pipeline's input (or the
  // watcher's next-up file when the input is dynamic).
  const inputForPreview = pipeline.inputFile?.dynamic
    ? (pipeline.inputFile.nextUp || 'newfile.csv')
    : (pipeline.inputFile?.name || 'input.csv');
  const resolve = resolveFilenamePattern;
  const seqNext = pipeline.output?.seqNext ?? pipeline.output?.seqStart ?? 1;
  const previewFilename = resolve(
    (filename || '').replace(/\.[^.]+$/, '') + '.' + format,
    { runId: lastRunId || 'R-026-A93', inputFile: inputForPreview, seq: seqNext }
  );

  React.useEffect(() => {
    if (!open) { setPhase('idle'); setProgress(0); setCurrentStepIdx(0); setResult(null); }
  }, [open]);

  // The server runs the whole pipeline in one request and reports no
  // per-step progress, so the step list only advances as a hint. It stops
  // at an HTTP Request step that waits for its result (polling), or at the
  // last step, and stays there until the run's real result arrives.
  const holdIdx = React.useMemo(() => {
    const i = ordered.findIndex(s => s.type === 'http_request' && s.config?.poll && (s.config?.url || '').trim());
    return i >= 0 ? i : ordered.length - 1;
  }, [ordered]);
  React.useEffect(() => {
    if (phase !== 'running') return;
    let i = 0;
    let tid;
    const tick = () => {
      setCurrentStepIdx(i);
      setProgress(((i + 1) / (ordered.length + 1)) * 100);
      if (i >= holdIdx) return;
      i++;
      tid = setTimeout(tick, 220);
    };
    tick();
    return () => clearTimeout(tid);
  }, [phase]);

  if (!open) return null;

  // "Append to input file" mode is configured on the Output block, not
  // re-editable here (unlike folder/filename/format) — read straight off
  // the live `pipeline.output` prop (which reflects any unsaved edit, not
  // just what's persisted) rather than duplicating its own state for it.
  const saveAs = pipeline.output?.saveAs || 'standalone';
  const appendSheetName = pipeline.output?.appendSheetName || '';
  const isAppendMode = saveAs === 'append_to_input';

  const start = async () => {
    setPhase('running'); setProgress(0); setCurrentStepIdx(0); setResult(null);
    const run = await onRun?.({ folder, filename, format, saveAs, appendSheetName });
    setResult(run || null);
    if (run?.status === 'done') {
      setProgress(100);
      if (openAfter) { onViewDetails?.(); onClose(); return; }
      setPhase('done');
    } else {
      setPhase('failed');
    }
  };
  const viewDetails = () => { onViewDetails?.(); onClose(); };
  // Closing mid-run would hide a run that is still going, so it's blocked.
  const close = () => { if (phase !== 'running') onClose(); };
  const pollingStep = ordered[currentStepIdx]?.type === 'http_request' && ordered[currentStepIdx]?.config?.poll;

  return (
    <>
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15, 17, 23, 0.65)', backdropFilter: 'blur(2px)',
      zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={close}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 620,
        background: C.app, border: `1px solid ${C.borderStrong}`, borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,.4)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 24px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>▶</span>
          <span style={{ color: C.fg1, fontWeight: 700, fontSize: '1.05rem' }}>Run pipeline</span>
          <Pill tone="ghost">{ordered.length} steps</Pill>
          <div style={{ flex: 1 }} />
          <IconButton title={phase === 'running' ? 'The run is still going' : 'Close'} disabled={phase === 'running'} onClick={close}><X size={14} /></IconButton>
        </div>

        <div style={{ padding: 24 }}>
          {phase === 'idle' && (
            <>
              <div style={{
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
                padding: '14px 16px', marginBottom: 18,
              }}>
                <Eyebrow>Pipeline</Eyebrow>
                <div style={{ color: C.fg1, fontSize: '.95rem', fontWeight: 600, marginTop: 4 }}>{pipeline.name}</div>
                <div style={{ color: C.fg4, fontSize: '.78rem', marginTop: 2 }}>
                  Input: <Code>{pipeline.inputFile.name}</Code> · {pipeline.inputFile.rows} rows
                </div>
              </div>

              {isAppendMode && (
                <Alert kind="info" style={{ marginBottom: 14 }}>
                  This run will append a new sheet, <strong>{appendSheetName || '(no name set)'}</strong>, to a
                  copy of <Code>{pipeline.inputFile.name}</Code> — set via the Output block's "Save as" setting.
                  The folder/filename/format fields below don't apply in this mode.
                </Alert>
              )}

              <div style={isAppendMode ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
              <FieldLabel>Output folder</FieldLabel>
              <div style={{ display: 'flex', gap: 8 }}>
                <TextInput mono value={folder} onChange={setFolder} placeholder="/path/to/output" />
                <Button kind="ghost" size="md" icon={<Folder size={14} />} onClick={() => setFolderPickerOpen(true)}>Browse…</Button>
              </div>

              <FieldLabel hint="Tokens: {run_id}, {input_name}, {date}, {seq:0001}… expand at run time">Filename pattern</FieldLabel>
              <TextInput mono value={filename} onChange={setFilename} />

              <div style={{
                marginTop: 6,
                background: 'var(--bg-input)',
                border: `1px solid ${C.accentMuted}`,
                borderRadius: 8,
                padding: '8px 11px',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '.78rem',
                color: C.accentBright,
                wordBreak: 'break-all',
              }}>
                <span style={{ color: C.fg5 }}>{(folder || '').replace(/\/$/, '') + '/'}</span>
                <span style={{ fontWeight: 600 }}>{previewFilename}</span>
              </div>

              <FieldLabel>Format</FieldLabel>
              <div style={{ display: 'flex', gap: 6 }}>
                {['csv', 'xlsx', 'xls'].map(f => (
                  <button key={f} onClick={f === 'xls' ? undefined : () => setFormat(f)} disabled={f === 'xls'} title={f === 'xls' ? PHASE2_TOOLTIP : undefined} style={{
                    opacity: f === 'xls' ? .45 : 1,
                    flex: 1, fontFamily: 'inherit', cursor: f === 'xls' ? 'not-allowed' : 'pointer',
                    background: format === f ? 'var(--info-bg)' : C.input,
                    color: format === f ? C.accentBright : C.fg3,
                    border: `1px solid ${format === f ? C.accentMuted : C.border}`,
                    borderRadius: 8, padding: '8px 12px',
                    fontSize: '.84rem', fontWeight: 600,
                    transition: 'all .15s',
                  }}>{f.toUpperCase()}{f === 'xls' ? ` · ${PHASE2_LABEL}` : ''}</button>
                ))}
              </div>
              {/* Only XLSX can hold every sheet — see server/app/engine/output.py. */}
              {format !== 'xlsx' && pipeline.inputFile?.buildTargetName && (
                <Alert kind="info" style={{ marginTop: 10 }}>
                  {format.toUpperCase()} holds a single sheet, so only the build target
                  (<Code>{pipeline.inputFile.buildTargetName}</Code>) is exported. Choose
                  <strong> XLSX</strong> to bundle every sheet into one file.
                </Alert>
              )}
              </div>

              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                color: C.fg3, fontSize: '.82rem', marginTop: 16,
              }}>
                <input type="checkbox" checked={openAfter} onChange={(e) => setOpenAfter(e.target.checked)}
                  style={{ accentColor: C.accent }} />
                Open run details when complete
              </label>

              <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                <Button kind="secondary" onClick={onClose}>Cancel</Button>
                <div style={{ flex: 1 }} />
                <Button kind="primary" size="lg" icon="▶" onClick={start}>Run pipeline</Button>
              </div>
            </>
          )}

          {phase === 'running' && (
            <>
              <Eyebrow>Running</Eyebrow>
              <div style={{ color: C.fg1, fontWeight: 700, fontSize: '1.1rem', margin: '4px 0 16px' }}>
                {ordered[currentStepIdx]?.label || 'Initialising…'}
              </div>
              {pollingStep && currentStepIdx === holdIdx && (
                <div style={{ color: C.fg4, fontSize: '.82rem', margin: '-10px 0 14px' }}>
                  Waiting for the API's result (polling). The run finishes when every batch has its result.
                </div>
              )}
              <div style={{
                height: 8, background: C.input, borderRadius: 4, overflow: 'hidden', marginBottom: 18,
              }}>
                <div style={{
                  height: '100%', width: `${progress}%`,
                  background: 'linear-gradient(90deg,#1d4ed8,#3b82f6)',
                  transition: 'width .2s ease',
                }} />
              </div>

              <div style={{
                background: C.input, border: `1px solid ${C.border}`, borderRadius: 8,
                padding: 12, fontFamily: "'JetBrains Mono', monospace",
                fontSize: '.74rem', color: C.fg3, lineHeight: 1.6, maxHeight: 160, overflowY: 'auto',
              }}>
                {ordered.slice(0, currentStepIdx + 1).map((s, i) => (
                  <div key={s.id} style={{ display: 'flex', gap: 10 }}>
                    <span style={{ color: i < currentStepIdx ? C.ok : C.warn, display: 'inline-flex' }}>
                      {i < currentStepIdx ? <Check size={13} /> : <Loader2 size={13} style={{ animation: 'omSpin 1s linear infinite' }} />}
                    </span>
                    <span style={{ color: C.fg5 }}>step {i + 1}</span>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {phase === 'done' && (
            <>
              <div style={{ textAlign: 'center', padding: '14px 0 24px' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'var(--ok-bg)', border: `2px solid ${C.ok}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: C.ok, marginBottom: 12,
                }}><Check size={28} strokeWidth={2.5} /></div>
                <div style={{ color: C.fg1, fontWeight: 700, fontSize: '1.1rem' }}>Pipeline run complete</div>
                <div style={{ color: C.fg4, fontSize: '.84rem', marginTop: 4 }}>
                  {result?.id ? <>Run ID <Code>{result.id}</Code></> : 'Output written successfully.'}
                </div>
              </div>

              <Alert kind="ok">
                Wrote <Code>{(result?.outputPath || '').split('/').pop() || 'output'}</Code>
                {result?.rowsOut != null && <> · {result.rowsOut} rows</>}
              </Alert>

              <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                <Button kind="secondary" onClick={onClose}>Close</Button>
                <div style={{ flex: 1 }} />
                <Button kind="primary" onClick={viewDetails}>View run details</Button>
              </div>
            </>
          )}

          {phase === 'failed' && (
            <>
              <Alert kind="error">
                The run failed{result?.error ? <>: {result.error}</> : '.'}
              </Alert>
              <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                <Button kind="secondary" onClick={onClose}>Close</Button>
                <div style={{ flex: 1 }} />
                {result?.id && <Button kind="primary" onClick={viewDetails}>View run details</Button>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    <FolderPickerModal
      open={folderPickerOpen}
      onClose={() => setFolderPickerOpen(false)}
      onPick={(p) => { setFolder(p); setFolderPickerOpen(false); }}
      currentPath={folder}
    />
    </>
  );
}

// ─── Pipelines screen (lists pipelines within a project) ─────────────────
export function PipelinesScreen({ project, allProjects, onOpenPipeline, onBack, onNewPipeline, onDuplicatePipeline, onRenamePipeline, onTogglePause, onDeletePipeline, onImportPipeline, onExportProject, onExportPipeline, onSetEnv, onSchedulePipeline }) {
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all'); // all | active | paused | draft
  const [newOpen, setNewOpen] = React.useState(false);

  const pipelines = project.pipelines || [];

  // Flatten all org pipelines with project name annotation for cross-project duplication.
  const allOrgPipelines = React.useMemo(() => {
    if (!allProjects) return pipelines;
    return allProjects.flatMap(pr =>
      (pr.pipelines || []).map(pl => ({ ...pl, projectName: pr.name }))
    );
  }, [allProjects, pipelines]);
  const filtered = pipelines
    .filter(p => !SHOW_PIPELINE_STATUS || statusFilter === 'all' || p.status === statusFilter)
    .filter(p => !search.trim() ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase()) ||
      p.inputFile.name.toLowerCase().includes(search.toLowerCase()));

  const activeCount = pipelines.filter(p => p.status === 'active').length;
  const pausedCount = pipelines.filter(p => p.status === 'paused').length;
  const draftCount  = pipelines.filter(p => p.status === 'draft').length;
  const errored     = pipelines.filter(p => p.lastRunStatus === 'error').length;

  return (
    <div style={{ padding: '24px clamp(14px, 3vw, 36px)', maxWidth: 1280, margin: '0 auto', width: '100%', boxSizing: 'border-box', minWidth: 0 }} data-screen-label={`Pipelines · ${project.name}`}>
      {/* Header */}
      <button onClick={onBack} style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: C.fg5, fontSize: '.78rem', fontFamily: 'inherit',
        padding: 0, marginBottom: 16,
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
        <span>←</span><span>All projects</span>
      </button>

      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: '20px 24px', marginBottom: 22,
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 12,
          background: C.input, border: `1px solid ${C.accentMuted}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, flexShrink: 0,
        }}>{project.icon}</div>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <Eyebrow>Project</Eyebrow>
          <h1 style={{
            color: C.fg1, fontSize: '1.45rem', fontWeight: 700, margin: '2px 0 4px',
            letterSpacing: '-.005em',
          }}>{project.name}</h1>
          <p style={{ color: C.fg4, fontSize: '.82rem', margin: 0 }}>{project.description}</p>
        </div>
        <div style={{ display: 'flex', gap: 22, alignSelf: 'stretch', paddingLeft: 16, borderLeft: `1px solid ${C.border}` }}>
          <StatBlock label="Pipelines" value={pipelines.length} />
          {SHOW_PIPELINE_STATUS && <StatBlock label="Active"    value={activeCount}  tone={activeCount > 0 ? 'ok' : 'neutral'} />}
          <StatBlock label="Erroring"  value={errored}      tone={errored > 0 ? 'warn' : 'neutral'} />
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 160px', maxWidth: 320 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.fg5, fontSize: '.85rem' }}>⌕</span>
          <TextInput value={search} onChange={setSearch} placeholder="Filter pipelines…" style={{ paddingLeft: 30 }} />
        </div>

        {/* Status segmented */}
        {SHOW_PIPELINE_STATUS && <div style={{
          display: 'flex', gap: 2, padding: 3,
          background: C.input, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
        }}>
          {[
            { id: 'all',    label: 'All',    count: pipelines.length },
            { id: 'active', label: 'Active', count: activeCount },
            { id: 'paused', label: 'Paused', count: pausedCount },
            { id: 'draft',  label: 'Draft',  count: draftCount },
          ].map(s => {
            const on = statusFilter === s.id;
            return (
              <button key={s.id} onClick={() => setStatusFilter(s.id)} style={{
                padding: '5px 11px',
                background: on ? 'var(--info-bg)' : 'transparent',
                color: on ? C.accentBright : C.fg4,
                border: 'none', borderRadius: 5, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: '.78rem', fontWeight: 600,
                display: 'inline-flex', gap: 6, alignItems: 'center',
              }}>
                {s.label}
                <span style={{
                  color: on ? C.accentBright : C.fg6,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '.7rem',
                  opacity: s.count === 0 && !on ? .55 : 1,
                }}>{s.count}</span>
              </button>
            );
          })}
        </div>}

        <div style={{ flex: 1 }} />
        <Button kind="ghost" size="md" icon="⤴" onClick={onExportProject} title="Export this project (with all its pipelines) as JSON">Export project</Button>
        <Button kind="ghost" size="md" icon="⤓" onClick={onImportPipeline}>Import pipeline</Button>
        <Button kind="primary" size="md" icon="＋" onClick={() => setNewOpen(true)}>New pipeline</Button>
      </div>

      {/* Pipeline list */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
        overflow: 'hidden',
      }}>
        {/* Header row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,2.2fr) minmax(0,1.5fr) minmax(0,1.1fr) minmax(0,1fr) minmax(0,.8fr) minmax(0,1fr) 32px',
          gap: 12, padding: '10px 18px',
          background: C.bar, borderBottom: `1px solid ${C.border}`,
          fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.08em',
          fontWeight: 700, color: C.fg5,
        }}>
          <div>Pipeline</div>
          <div>Input</div>
          <div>Schedule</div>
          <div>Last run</div>
          <div style={{ textAlign: 'right' }}>Rows</div>
          <div>Env</div>
          <div></div>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: C.fg5 }}>
            <div style={{ display: 'flex', justifyContent: 'center', opacity: .4, marginBottom: 8 }}><Search size={28} /></div>
            <div style={{ color: C.fg3, fontWeight: 600, marginBottom: 4 }}>
              No pipelines match
            </div>
            <div style={{ fontSize: '.78rem' }}>Try a different filter or create a new pipeline.</div>
          </div>
        )}

        {filtered.map((pip, i) => (
          <PipelineRow
            key={pip.id}
            pipeline={pip}
            isLast={i === filtered.length - 1}
            onOpen={() => onOpenPipeline(pip)}
            onRename={() => onRenamePipeline?.(pip)}
            onDuplicate={() => onDuplicatePipeline(pip)}
            onTogglePause={() => onTogglePause(pip)}
            onDelete={() => onDeletePipeline(pip)}
            onSetEnv={(env) => onSetEnv?.(pip.id, env)}
            onSchedule={() => onSchedulePipeline?.(pip)}
          />
        ))}
      </div>

      <NewPipelineModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        existingPipelines={allOrgPipelines}
        onCreate={(spec) => { setNewOpen(false); onNewPipeline(spec); }}
      />
    </div>
  );
}

function StatBlock({ label, value, tone = 'neutral' }) {
  const color = tone === 'warn' ? C.warn : tone === 'ok' ? C.ok : C.fg1;
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '1.4rem', fontWeight: 600,
        color, marginTop: 2,
      }}>{value}</div>
    </div>
  );
}

function PipelineRow({ pipeline, isLast, onOpen, onRename, onDuplicate, onTogglePause, onDelete, onSetEnv, onSchedule }) {
  const [hover, setHover] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ top: 0, right: 0 });

  // The pipelines grid wraps the table in `overflow: hidden` so the
  // rounded corners clip the rows. That also clips an absolutely-positioned
  // dropdown though — so the menu is rendered with `position: fixed`
  // anchored to the trigger button's viewport rect.
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const openMenu = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      setMenuPos({
        top:   Math.round(r.bottom + 6),
        right: Math.round(window.innerWidth - r.right),
      });
    }
    setMenuOpen(true);
  };
  // Once the menu has rendered, measure it: if it would run past the bottom
  // of the window (last rows of the list), flip it above the trigger instead,
  // and clamp so it always stays fully on screen.
  React.useLayoutEffect(() => {
    if (!menuOpen || !menuRef.current || !triggerRef.current) return;
    const h = menuRef.current.offsetHeight;
    const r = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    let top = Math.round(r.bottom + 6);
    if (top + h > vh - 8) top = Math.round(r.top - h - 6);
    top = Math.max(8, Math.min(top, vh - h - 8));
    setMenuPos(p => (p.top === top ? p : { ...p, top }));
  }, [menuOpen]);
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
        && !triggerRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onScrollOrResize = () => setMenuOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [menuOpen]);

  const fmt = (pipeline.inputFile.format || 'csv').toLowerCase();
  const fmtPal = fmt === 'xls'
    ? { bg: 'var(--warn-bg)', bd: 'var(--warn-border)', fg: C.warn }
    : { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,2.2fr) minmax(0,1.5fr) minmax(0,1.1fr) minmax(0,1fr) minmax(0,.8fr) minmax(0,1fr) 32px',
        gap: 12, padding: '14px 18px',
        background: hover ? C.input : 'transparent',
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSubtle}`,
        alignItems: 'center',
        cursor: 'pointer', transition: 'background-color .12s',
      }}
      onClick={onOpen}
    >
      {/* Name + status + description */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{
            color: C.fg1, fontWeight: 600, fontSize: '.88rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{pipeline.name}</span>
          {SHOW_PIPELINE_STATUS && <PipelineStatusBadge status={pipeline.status} />}
        </div>
        <div style={{
          color: C.fg5, fontSize: '.74rem', lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{pipeline.description}</div>
      </div>

      {/* Input file */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{
          flexShrink: 0,
          background: fmtPal.bg, border: `1px solid ${fmtPal.bd}`,
          color: fmtPal.fg, fontSize: '.6rem', fontWeight: 700,
          letterSpacing: '.04em', padding: '2px 5px', borderRadius: 3,
          textTransform: 'uppercase',
        }}>{fmt}</span>
        <span style={{
          color: C.fg3, fontSize: '.76rem',
          fontFamily: "'JetBrains Mono', monospace",
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          minWidth: 0,
        }}>{pipeline.inputFile.name}</span>
      </div>

      {/* Schedule (scheduling is Phase 2 — shown dimmed) */}
      <div title={PHASE2_TOOLTIP} style={{
        color: C.fg3, fontSize: '.76rem', opacity: .45,
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}>
        <span style={{ color: C.fg6, display: 'inline-flex' }}>
          {pipeline.schedule === 'Manual' ? <Hand size={12} /> : <Clock size={12} />}
        </span>
        {pipeline.schedule}
      </div>

      {/* Last run + status */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <LastRunChip status={pipeline.lastRunStatus} />
        </div>
        <div style={{
          color: C.fg5, fontSize: '.7rem',
          fontFamily: "'JetBrains Mono', monospace",
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={pipeline.lastRunAt ? new Date(pipeline.lastRunAt).toLocaleString() : undefined}>{runAgo(pipeline.lastRunAt)}</div>
      </div>

      {/* Rows */}
      <div style={{
        textAlign: 'right',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '.82rem', color: pipeline.rowsProcessed > 0 ? C.fg2 : C.fg5,
      }}>{(pipeline.rowsProcessed || 0).toLocaleString()}</div>

      {/* Env (Test / Prod publish toggle) */}
      <div onClick={(e) => e.stopPropagation()}>
        <EnvToggle
          size="sm"
          value={pipeline.env || 'test'}
          onChange={(env) => onSetEnv?.(env)}
        />
      </div>

      {/* Row menu */}
      <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <button
          ref={triggerRef}
          onClick={() => menuOpen ? setMenuOpen(false) : openMenu()}
          title="Pipeline actions"
          style={{
            width: 28, height: 28, borderRadius: 6,
            background: menuOpen ? C.input : 'transparent',
            color: C.fg4, border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: '1.05rem', lineHeight: 1,
            padding: 0,
          }}
        >⋯</button>
        {menuOpen && (
          <div ref={menuRef} style={{
            position: 'fixed', top: menuPos.top, right: menuPos.right,
            background: C.app, border: `1px solid ${C.borderStrong}`,
            borderRadius: 8, padding: 4, zIndex: 60, minWidth: 200,
            boxShadow: '0 12px 32px rgba(0,0,0,.5)',
          }}>
            <MenuItem icon={<Play size={14} />} onClick={() => { setMenuOpen(false); onOpen(); }}>Open</MenuItem>
            <MenuItem icon={<Pencil size={14} />} onClick={() => { setMenuOpen(false); onRename?.(); }}>Rename…</MenuItem>
            <MenuItem icon={<Clock size={14} />} disabled onClick={() => { setMenuOpen(false); onSchedule?.(); }}>Schedule… <Phase2Tag /></MenuItem>
            <MenuItem icon={<Copy size={14} />} onClick={() => { setMenuOpen(false); onDuplicate(); }}>Duplicate…</MenuItem>
            <MenuItem icon={pipeline.status === 'paused' ? <Play size={14} /> : <Pause size={14} />} disabled onClick={() => { setMenuOpen(false); onTogglePause(); }}>
              {pipeline.status === 'paused' ? 'Resume schedule' : 'Pause schedule'}
             <Phase2Tag /></MenuItem>
            <div style={{ height: 1, background: C.border, margin: '4px 0' }} />
            <MenuItem icon={<Trash2 size={14} />} danger onClick={() => { setMenuOpen(false); onDelete(); }}>Delete pipeline</MenuItem>
          </div>
        )}
      </div>
    </div>
  );
}

function Phase2Tag() {
  return <span style={{ marginLeft: 'auto', fontSize: '.6rem', fontWeight: 700, color: C.fg5, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px' }}>{PHASE2_LABEL}</span>;
}

function MenuItem({ icon, children, onClick, danger, disabled }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? PHASE2_TOOLTIP : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        opacity: disabled ? .45 : 1,
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '7px 10px',
        background: hover && !disabled ? (danger ? 'var(--error-bg)' : C.input) : 'transparent',
        color: danger ? (hover ? C.error : '#f87171') : C.fg2,
        border: 'none', borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', fontSize: '.82rem', fontWeight: 500,
        textAlign: 'left',
      }}
    >
      <span style={{ width: 14, textAlign: 'center', opacity: .8 }}>{icon}</span>
      {children}
    </button>
  );
}

function PipelineStatusBadge({ status }) {
  const p = {
    active: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok,   label: 'Active' },
    paused: { bg: 'var(--warn-bg)', bd: 'var(--warn-border)', fg: C.warn, label: 'Paused' },
    draft:  { bg: C.input,   bd: C.border,  fg: C.fg4,  label: 'Draft'  },
  }[status] || { bg: C.input, bd: C.border, fg: C.fg5, label: status };
  return (
    <span style={{
      background: p.bg, border: `1px solid ${p.bd}`, color: p.fg,
      fontSize: '.6rem', fontWeight: 700, letterSpacing: '.06em',
      padding: '1px 6px', borderRadius: 4,
      textTransform: 'uppercase', flexShrink: 0,
    }}>{p.label}</span>
  );
}

// ─── New pipeline modal ─────────────────────────────────────────────────
const NEW_TEMPLATES = [
  { id: 'blank',  icon: <Circle size={15} />, label: 'Blank pipeline',     hint: 'Just a source step. Build from scratch.' },
  { id: 'clean',  icon: <Sparkles size={15} />, label: 'Cleanse + dedupe',   hint: 'Trim → normalize → dedupe → output. 6 steps.' },
  { id: 'recon',  icon: <ArrowLeftRight size={15} />, label: 'Two-file reconciler', hint: 'Join two files, diff columns, flag mismatches.' },
  { id: 'split',  icon: <Columns2 size={15} />, label: 'Split by column',     hint: 'Source → filter into N outputs by category.' },
];

export function NewPipelineModal({ open, onClose, existingPipelines, onCreate }) {
  const [mode, setMode] = React.useState('blank'); // blank | duplicate | template
  const [templateId, setTemplateId] = React.useState('clean');
  const [dupSourceId, setDupSourceId] = React.useState(existingPipelines[0]?.id || null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setMode('blank');
      setTemplateId('clean');
      setDupSourceId(existingPipelines[0]?.id || null);
      setName('');
      setDescription('');
    }
  }, [open, existingPipelines]);

  if (!open) return null;

  const canCreate = name.trim().length > 0 && (mode !== 'duplicate' || dupSourceId);

  const submit = () => {
    if (!canCreate) return;
    onCreate({
      mode, templateId, dupSourceId,
      name: name.trim(),
      description: description.trim(),
    });
  };

  return (
    <div
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15,17,23,.6)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(680px, 100%)', maxHeight: '90vh',
          background: C.app, border: `1px solid ${C.borderStrong}`,
          borderRadius: 12, boxShadow: '0 24px 64px rgba(0,0,0,.55)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 10, background: C.bar,
        }}>
          <span style={{ fontSize: 18 }}>＋</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.fg1, fontWeight: 700, fontSize: '.95rem' }}>New pipeline</div>
            <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 1 }}>
              Add a new data flow to this project. You can pick an input file after.
            </div>
          </div>
          <IconButton title="Close (Esc)" onClick={onClose}><X size={14} /></IconButton>
        </div>

        <div style={{ padding: 22, overflowY: 'auto' }}>
          {/* Mode picker */}
          <Eyebrow>Start from</Eyebrow>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 6 }}>
            <ModeCard label="Blank" hint="Just a source step." selected={mode === 'blank'}    onClick={() => setMode('blank')}    icon={<Circle size={17} />} />
            <ModeCard label="Template" hint={`${PHASE2_LABEL} — starter flows not available yet.`} selected={false} onClick={() => {}} icon={<LayoutTemplate size={17} />} disabled />
            <ModeCard label="Duplicate" hint="Copy an existing pipeline." selected={mode === 'duplicate'} onClick={() => setMode('duplicate')} icon={<Copy size={17} />} disabled={existingPipelines.length === 0} />
          </div>

          {mode === 'template' && (
            <div style={{ marginTop: 16 }}>
              <Eyebrow>Template</Eyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {NEW_TEMPLATES.map(t => (
                  <button key={t.id} onClick={() => setTemplateId(t.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px',
                    background: templateId === t.id ? 'var(--info-bg)' : C.input,
                    border: `1px solid ${templateId === t.id ? C.accentMuted : C.border}`,
                    borderRadius: 8, cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left',
                    color: 'inherit',
                  }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: 6,
                      background: C.input, border: `1px solid ${C.borderStrong}`,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      color: templateId === t.id ? C.accentBright : C.fg3,
                      fontSize: '.9rem', flexShrink: 0,
                    }}>{t.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: C.fg1, fontWeight: 600, fontSize: '.82rem' }}>{t.label}</div>
                      <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 1 }}>{t.hint}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === 'duplicate' && (
            <div style={{ marginTop: 16 }}>
              <Eyebrow>Copy from</Eyebrow>
              <Select
                value={dupSourceId || ''}
                onChange={setDupSourceId}
                optgroups={(() => {
                  const groups = [];
                  const seen = new Map();
                  for (const p of existingPipelines) {
                    const key = p.projectName || 'This project';
                    if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ label: key, options: [] }); }
                    groups[seen.get(key)].options.push({ value: p.id, label: `${p.name}  ·  ${p.steps.length} steps` });
                  }
                  return groups;
                })()}
                style={{ marginTop: 6, width: '100%' }}
              />
              <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 6 }}>
                All steps, input file, and output settings are copied. Last-run history is not.
              </div>
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <FieldLabel>Pipeline name</FieldLabel>
            <TextInput value={name} onChange={setName} placeholder="e.g. EU customer cleanse" autoFocus />

            <FieldLabel hint="Optional. Shows in the pipeline list.">Description</FieldLabel>
            <TextInput value={description} onChange={setDescription} placeholder="What this pipeline does, who owns it…" />
          </div>
        </div>

        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${C.border}`, background: C.bar,
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <Button kind="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button kind="primary" size="sm" disabled={!canCreate} onClick={submit}>Create pipeline</Button>
        </div>
      </div>
    </div>
  );
}

function ModeCard({ label, hint, icon, selected, onClick, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        padding: '12px 12px',
        background: selected ? 'var(--info-bg)' : C.input,
        border: `1px solid ${selected ? C.accentMuted : C.border}`,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', textAlign: 'left',
        opacity: disabled ? .45 : 1,
        color: 'inherit',
      }}
    >
      <div style={{
        fontSize: '1.05rem', marginBottom: 6,
        color: selected ? C.accentBright : C.fg3,
      }}>{icon}</div>
      <div style={{ color: C.fg1, fontWeight: 600, fontSize: '.82rem' }}>{label}</div>
      <div style={{ color: C.fg5, fontSize: '.7rem', marginTop: 2, lineHeight: 1.4 }}>{hint}</div>
    </button>
  );
}

// Also edits an existing project's name and description: pass `project`
// (prefilled, icon picker hidden) and `onSave`.
export function NewProjectModal({ open, onClose, onCreate, onSave, project = null, organizationName = 'acme-data-eng' }) {
  const editing = !!project;
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [icon, setIcon] = React.useState('✦');

  React.useEffect(() => {
    if (open) {
      setName(project?.name || '');
      setDescription(project?.description || '');
      setIcon(project?.icon || '✦');
    }
  }, [open, project]);

  if (!open) return null;

  const canCreate = name.trim().length > 0;
  const submit = () => {
    if (!canCreate) return;
    if (editing) {
      onSave({ name: name.trim(), description: description.trim() });
      return;
    }
    onCreate({
      name: name.trim(),
      description: description.trim(),
      icon,
    });
  };

  const PROJECT_ICONS = ['✦', '👥', '📦', '🧾', '📊', '🏢', '🗄', '🔎', '⚙', '⚛'];

  return (
    <div
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
      }}
      tabIndex={-1}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15,17,23,.6)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)',
          background: C.app, border: `1px solid ${C.borderStrong}`,
          borderRadius: 12, boxShadow: '0 24px 64px rgba(0,0,0,.55)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 22px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 12, background: C.bar,
        }}>
          <span style={{
            width: 34, height: 34, borderRadius: 8,
            background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: C.accentBright, fontSize: '1.05rem',
            flexShrink: 0,
          }}>{editing ? <Pencil size={15} /> : '＋'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: C.fg1, fontWeight: 700, fontSize: '.95rem' }}>{editing ? 'Edit project' : 'New project'}</div>
            <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 1 }}>
              {editing ? 'Change the project\'s name and description.' : 'A project holds the pipelines for one business domain.'}
            </div>
          </div>
          <IconButton title="Close (Esc)" onClick={onClose}><X size={14} /></IconButton>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px' }}>
          {/* Organization badge */}
          <div style={{
            color: C.fg5, fontSize: '.66rem',
            textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
            marginBottom: 6,
          }}>Organization</div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: C.input, border: `1px solid ${C.borderStrong}`,
            borderRadius: 8, padding: '9px 12px',
          }}>
            <span style={{
              width: 24, height: 24, borderRadius: 5,
              background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: C.accentBright, fontSize: '.7rem', fontWeight: 700,
              flexShrink: 0,
            }}>{organizationName.slice(0, 1).toUpperCase()}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                color: C.fg1, fontSize: '.82rem', fontWeight: 600,
                fontFamily: "'JetBrains Mono', monospace",
              }}>{organizationName}</div>
              <div style={{ color: C.fg6, fontSize: '.7rem', marginTop: 1 }}>
                Projects belong to the active organization.
              </div>
            </div>
          </div>

          {/* Name */}
          <div style={{ marginTop: 18 }}>
            <label style={{
              display: 'block',
              color: C.fg5, fontSize: '.66rem',
              textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
              marginBottom: 6,
            }}>Project name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Marketing Lead Cleanse"
              autoFocus
              maxLength={80}
              style={{
                width: '100%',
                background: C.input,
                border: `1px solid ${name.trim() ? C.borderStrong : C.border}`,
                borderRadius: 8,
                padding: '9px 12px',
                color: C.fg1, fontSize: '.9rem',
                fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginTop: 14 }}>
            <label style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              color: C.fg5, fontSize: '.66rem',
              textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
              marginBottom: 6,
            }}>
              <span>Description</span>
              <span style={{ color: C.fg6, letterSpacing: '.06em' }}>Optional</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What lives in this project? (helps teammates find it)"
              rows={3}
              maxLength={240}
              style={{
                width: '100%',
                background: C.input,
                border: `1px solid ${C.borderStrong}`,
                borderRadius: 8,
                padding: '9px 12px',
                color: C.fg1, fontSize: '.84rem',
                fontFamily: 'inherit', outline: 'none', resize: 'vertical',
                minHeight: 64, boxSizing: 'border-box',
              }}
            />
            <div style={{
              color: C.fg6, fontSize: '.68rem', textAlign: 'right', marginTop: 4,
              fontFamily: "'JetBrains Mono', monospace",
            }}>{description.length}/240</div>
          </div>

          {/* Icon picker (new projects only) */}
          {!editing && <div style={{ marginTop: 6 }}>
            <label style={{
              display: 'block',
              color: C.fg5, fontSize: '.66rem',
              textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
              marginBottom: 6,
            }}>Icon</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PROJECT_ICONS.map(g => {
                const on = icon === g;
                return (
                  <button
                    key={g}
                    onClick={() => setIcon(g)}
                    style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: on ? 'var(--info-bg)' : C.input,
                      border: `1px solid ${on ? C.accentMuted : C.borderStrong}`,
                      cursor: 'pointer',
                      fontSize: '1rem',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'inherit',
                    }}
                  >{g}</button>
                );
              })}
            </div>
          </div>}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 22px', borderTop: `1px solid ${C.border}`,
          background: C.bar,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            flex: 1, color: C.fg5, fontSize: '.72rem',
            fontFamily: "'JetBrains Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {organizationName}/{(name.trim() || '…').toLowerCase().replace(/[^a-z0-9]+/g, '-')}
          </div>
          <Button kind="ghost"   size="sm" onClick={onClose}>Cancel</Button>
          <Button kind="primary" size="sm" disabled={!canCreate} onClick={submit}>
            {editing ? 'Save' : 'Create project'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── SSO sign-in ─────────────────────────────────────────────────────
// What a signed-out visitor sees at the app's normal URL: sign-in happens
// through RAPFlow, which opens the app with ?token=… (handled in App.jsx).
// The mock email login lives at its own URL (MOCK_LOGIN_PATH in App.jsx).
export function SsoSignInScreen({ error }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: `radial-gradient(ellipse at top, rgba(59,130,246,.10), transparent 60%), ${C.app}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        width: 'min(420px, 100%)',
        background: C.bar,
        border: `1px solid ${C.borderStrong}`,
        borderRadius: 14,
        boxShadow: '0 24px 64px rgba(0,0,0,.55)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '24px 28px 20px',
          background: 'linear-gradient(180deg, rgba(59,130,246,.07), transparent)',
          borderBottom: `1px solid ${C.borderSubtle}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <Logo size={26} />
            <div style={{
              color: C.fg1, fontWeight: 700, fontSize: '1rem', letterSpacing: '-.005em',
            }}>Data<span style={{ color: C.fg5, fontWeight: 400 }}>Studio</span></div>
          </div>
          <div style={{ color: C.fg1, fontWeight: 700, fontSize: '1.2rem', letterSpacing: '-.01em' }}>
            Sign in
          </div>
          <div style={{ color: C.fg4, fontSize: '.82rem', marginTop: 4 }}>
            Sign-in happens through RAPFlow.
          </div>
        </div>
        <div style={{ padding: '20px 28px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <Alert kind="error">Single sign-on failed: {error}</Alert>}
          <div style={{ color: C.fg3, fontSize: '.85rem', lineHeight: 1.55 }}>
            Open DataStudio from RAPFlow and you will be signed in automatically.
            {error ? ' Try opening it from RAPFlow again; if it keeps failing, ask your administrator to check that your RAPFlow email has a DataStudio account.' : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mock login screen ─────────────────────────────────────────────────
// Served at MOCK_LOGIN_PATH (App.jsx); the server accepts any email and
// password. Layout follows RAPFlow's login page (web/Login.tsx,
// LoginForm.tsx, Layout.tsx): centred card, product title, "Login" heading,
// labelled fields with inline errors, one button, legal footer.
const LOGIN_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function LoginField({ id, label, error, children }) {
  return (
    <div>
      <label htmlFor={id} style={{
        display: 'block', marginBottom: 8,
        color: C.fg1, fontSize: '.875rem', fontWeight: 600,
      }}>
        {label} <span style={{ color: C.error }}>*</span>
      </label>
      {children}
      {error && (
        <div style={{ color: C.error, fontSize: '.75rem', marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
}

const loginInputStyle = (error) => ({
  width: '100%', boxSizing: 'border-box',
  background: C.input,
  border: `1px solid ${error ? C.error : C.borderStrong}`,
  borderRadius: 4, padding: '8.5px 14px',
  color: C.fg1, fontSize: '.9rem', fontFamily: 'inherit',
  outline: 'none',
});

const loginLinkStyle = { color: C.accentBright, textDecoration: 'none' };

export function LoginScreen({ onLogin, organizationName = 'acme-data-eng' }) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPw, setShowPw] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);

  // Same rules as RAPFlow's login form (email required and valid, password
  // at least 8 characters).
  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = 'Required';
    else if (!LOGIN_EMAIL_RE.test(email.trim())) e.email = 'Invalid email';
    if (password.length < 8) e.password = 'password must be at least 8 characters long';
    return e;
  };

  // Derive a display name from the email prefix (capitalise + replace . with space).
  const displayName = (em) => {
    const local = (em.split('@')[0] || '').replace(/[._]+/g, ' ');
    return local.replace(/\b\w/g, c => c.toUpperCase()) || 'User';
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await onLogin({
        name: displayName(email.trim()),
        email: email.trim(),
        password,
        organization: organizationName,
      });
    } finally {
      setBusy(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div style={{
      minHeight: '100vh', boxSizing: 'border-box',
      background: C.app,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '32px 16px',
    }}>
      <div style={{
        width: 'min(440px, 100%)', boxSizing: 'border-box',
        padding: 32,
        background: C.bar,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        boxShadow: '0 6px 20px rgba(0,0,0,.35)',
      }}>
        {/* Product */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
          marginBottom: 32,
        }}>
          <Logo size={36} />
          <h1 style={{
            margin: 0, color: C.fg1, fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-.01em',
          }}>DataStudio</h1>
        </div>

        <h2 style={{ margin: '0 0 24px', color: C.fg1, fontWeight: 600, fontSize: '1.5rem' }}>
          Login
        </h2>

        <form onSubmit={submit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <LoginField id="email" label="Email Address" error={errors.email}>
            <input
              id="email" type="email" autoFocus autoComplete="username"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors(er => ({ ...er, email: undefined })); }}
              placeholder="Enter your email address"
              style={loginInputStyle(errors.email)}
            />
          </LoginField>

          <LoginField id="password" label="Password" error={errors.password}>
            <div style={{ position: 'relative' }}>
              <input
                id="password" type={showPw ? 'text' : 'password'} autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrors(er => ({ ...er, password: undefined })); }}
                placeholder="Enter your password"
                style={{ ...loginInputStyle(errors.password), paddingRight: 44 }}
              />
              <button type="button"
                aria-label="toggle password visibility"
                onClick={() => setShowPw(s => !s)}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', borderRadius: '50%',
                  color: C.fg4, cursor: 'pointer', padding: 6, display: 'inline-flex',
                }}
              >{showPw ? <EyeOff size={20} /> : <Eye size={20} />}</button>
            </div>
          </LoginField>

          <button type="submit" disabled={busy}
            style={{
              marginTop: 16, width: '100%',
              background: C.accentStrong, border: 'none', borderRadius: 4,
              padding: '7px 14px', minHeight: 32,
              color: '#fff', fontSize: '.875rem', fontWeight: 600, fontFamily: 'inherit',
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? .7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {busy
              ? <Loader2 size={16} style={{ animation: 'omSpin 1s linear infinite' }} />
              : 'Login'}
          </button>
        </form>

        {/* Legal footer */}
        <div style={{
          marginTop: 32, display: 'flex', flexDirection: 'column', gap: 8,
          color: C.fg4, fontSize: '.75rem', lineHeight: 1.5, textAlign: 'center',
        }}>
          <div>
            By continuing, you agree to the Rapid Acceleration Partners, Inc.{' '}
            <a href="https://rapidautomation.ai/privacy-policy" target="_blank" rel="noopener noreferrer" style={loginLinkStyle}>Privacy Policy</a>{' '}
            and relevant usage terms.
          </div>
          <div>
            © {year} Rapid Acceleration Partners, Inc. All rights reserved.{' '}
            <a href="https://rapidautomation.ai/copyright" target="_blank" rel="noopener noreferrer" style={loginLinkStyle}>Copyright</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Schedule pipeline modal ───────────────────────────────────────────
// Form for changing how often a pipeline runs. Frequency-driven UI with
// a `cron` escape hatch. Emits a short, human label ("Daily · 09:30 UTC")
// that the pipelines table renders verbatim.
export function SchedulePipelineModal({ open, onClose, pipeline, onSave }) {
  const initial = parseSchedule(pipeline?.schedule || 'Manual');
  const [freq, setFreq] = React.useState(initial.freq);
  const [time, setTime] = React.useState(initial.time);
  const [minute, setMinute] = React.useState(initial.minute);
  const [weekday, setWeekday] = React.useState(initial.weekday);
  const [day, setDay] = React.useState(initial.day);
  const [tz, setTz] = React.useState(initial.tz);
  const [cron, setCron] = React.useState(initial.cron);

  React.useEffect(() => {
    if (!open) return;
    const fresh = parseSchedule(pipeline?.schedule || 'Manual');
    setFreq(fresh.freq); setTime(fresh.time); setMinute(fresh.minute);
    setWeekday(fresh.weekday); setDay(fresh.day); setTz(fresh.tz); setCron(fresh.cron);
  }, [open, pipeline]);

  if (!open || !pipeline) return null;

  const WEEKDAYS = [
    { id: 'mon', short: 'Mon' }, { id: 'tue', short: 'Tue' }, { id: 'wed', short: 'Wed' },
    { id: 'thu', short: 'Thu' }, { id: 'fri', short: 'Fri' }, { id: 'sat', short: 'Sat' },
    { id: 'sun', short: 'Sun' },
  ];
  const TIMEZONES = ['UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Asia/Singapore', 'Asia/Tokyo'];

  const buildLabel = () => {
    if (freq === 'manual')  return 'Manual';
    if (freq === 'hourly')  return `Hourly · :${String(minute).padStart(2,'0')}`;
    if (freq === 'daily')   return `Daily · ${time} ${tz}`;
    if (freq === 'weekly')  return `Weekly · ${WEEKDAYS.find(w => w.id === weekday)?.short} ${time} ${tz}`;
    if (freq === 'monthly') return `Monthly · day ${day} ${time} ${tz}`;
    if (freq === 'cron')    return `Cron · ${cron}`;
    return 'Manual';
  };
  const label = buildLabel();

  const nextRunPreview = () => {
    if (freq === 'manual') return 'Not scheduled. Triggered on demand.';
    if (freq === 'hourly') return `Every hour at :${String(minute).padStart(2,'0')}`;
    if (freq === 'daily')  return `Tomorrow at ${time} ${tz}`;
    if (freq === 'weekly') {
      const w = WEEKDAYS.find(x => x.id === weekday);
      return `Next ${w?.short} at ${time} ${tz}`;
    }
    if (freq === 'monthly') {
      const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
      return `${day}${suffix} of next month at ${time} ${tz}`;
    }
    if (freq === 'cron') return cron.trim() ? 'When the cron expression matches' : 'Enter a cron expression above';
    return '';
  };
  const cronValid = freq !== 'cron' || /^[\d*/,\-\s?L#]+(\s+[\d*/,\-\s?L#]+){4,5}$/.test(cron.trim());

  const submit = () => {
    if (!cronValid) return;
    onSave({
      schedule: label,
      scheduleConfig: { freq, minute, time, weekday, day, tz, cron: cron.trim() },
      // Picking a non-manual schedule promotes a draft pipeline to active.
      // Switching back to manual on an active pipeline pauses it.
      status: freq === 'manual'
        ? (pipeline.status === 'active' ? 'paused' : pipeline.status)
        : (pipeline.status === 'draft' ? 'active' : pipeline.status),
    });
  };

  return (
    <div
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
      style={{
        position: 'fixed', inset: 0, zIndex: 110,
        background: 'rgba(15,17,23,.62)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(620px, 100%)',
          background: C.app, border: `1px solid ${C.borderStrong}`,
          borderRadius: 12, boxShadow: '0 24px 64px rgba(0,0,0,.55)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 22px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 12, background: C.bar,
        }}>
          <span style={{
            width: 34, height: 34, borderRadius: 8,
            background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: C.accentBright, fontSize: '1.05rem', flexShrink: 0,
          }}><Clock size={16} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: C.fg1, fontWeight: 700, fontSize: '.95rem' }}>Schedule pipeline</div>
            <div style={{
              color: C.fg5, fontSize: '.74rem', marginTop: 1,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{pipeline.name}</div>
          </div>
          <IconButton title="Close (Esc)" onClick={onClose}><X size={14} /></IconButton>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px', overflowY: 'auto', minHeight: 0, flex: 1 }}>
          <FieldLabel>Run frequency</FieldLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {[
              { id: 'manual',  label: 'Manual',  icon: <Hand size={15} />, hint: 'On demand' },
              { id: 'hourly',  label: 'Hourly',  icon: <Clock size={15} />, hint: 'Every hour' },
              { id: 'daily',   label: 'Daily',   icon: <Sunrise size={15} />, hint: 'Every day' },
              { id: 'weekly',  label: 'Weekly',  icon: <CalendarDays size={15} />, hint: 'Pick a day' },
              { id: 'monthly', label: 'Monthly', icon: <CalendarRange size={15} />, hint: 'Day of month' },
              { id: 'cron',    label: 'Cron',    icon: <Settings2 size={15} />, hint: 'Custom expr.' },
            ].map(f => {
              const on = freq === f.id;
              return (
                <button key={f.id} onClick={() => setFreq(f.id)}
                  style={{
                    padding: '10px 11px',
                    background: on ? 'var(--info-bg)' : C.input,
                    border: `1px solid ${on ? C.accentMuted : C.borderStrong}`,
                    borderRadius: 8,
                    cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ display: 'inline-flex', color: on ? C.accentBright : C.fg3 }}>{f.icon}</span>
                    <span style={{
                      color: on ? C.accentBright : C.fg1,
                      fontSize: '.82rem', fontWeight: 700,
                    }}>{f.label}</span>
                  </div>
                  <div style={{ color: C.fg5, fontSize: '.68rem' }}>{f.hint}</div>
                </button>
              );
            })}
          </div>

          {freq === 'hourly' && (
            <>
              <FieldLabel>Minute of the hour</FieldLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="number" min={0} max={59} value={minute}
                  onChange={(e) => setMinute(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                  style={schedNumStyle}
                />
                <span style={{ color: C.fg5, fontSize: '.78rem' }}>
                  Runs at <Code>:{String(minute).padStart(2,'0')}</Code> every hour.
                </span>
              </div>
            </>
          )}

          {(freq === 'daily' || freq === 'weekly' || freq === 'monthly') && (
            <>
              {freq === 'weekly' && (
                <>
                  <FieldLabel>Day of week</FieldLabel>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {WEEKDAYS.map(w => {
                      const on = weekday === w.id;
                      return (
                        <button key={w.id} onClick={() => setWeekday(w.id)}
                          style={{
                            padding: '7px 14px',
                            background: on ? 'var(--info-bg)' : C.input,
                            border: `1px solid ${on ? C.accentMuted : C.borderStrong}`,
                            color: on ? C.accentBright : C.fg2,
                            borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
                            fontSize: '.78rem', fontWeight: 600,
                          }}
                        >{w.short}</button>
                      );
                    })}
                  </div>
                </>
              )}

              {freq === 'monthly' && (
                <>
                  <FieldLabel hint="Months with fewer days fall back to the last day">Day of month</FieldLabel>
                  <input type="number" min={1} max={31} value={day}
                    onChange={(e) => setDay(Math.max(1, Math.min(31, Number(e.target.value) || 1)))}
                    style={schedNumStyle}
                  />
                </>
              )}

              <FieldLabel>Time</FieldLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="time" value={time}
                  onChange={(e) => setTime(e.target.value)}
                  style={{
                    background: C.input,
                    border: `1px solid ${C.borderStrong}`,
                    borderRadius: 8,
                    padding: '8px 11px',
                    color: C.fg1, fontSize: '.86rem',
                    fontFamily: "'JetBrains Mono', monospace",
                    outline: 'none', boxSizing: 'border-box',
                    width: 140, colorScheme: 'dark',
                  }}
                />
                <select value={tz} onChange={(e) => setTz(e.target.value)}
                  style={{
                    flex: 1,
                    background: C.input,
                    border: `1px solid ${C.borderStrong}`,
                    borderRadius: 8,
                    padding: '8px 11px',
                    color: C.fg1, fontSize: '.84rem',
                    fontFamily: "'JetBrains Mono', monospace",
                    outline: 'none', boxSizing: 'border-box',
                  }}
                >
                  {TIMEZONES.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
            </>
          )}

          {freq === 'cron' && (
            <>
              <FieldLabel hint="Standard 5-field cron: minute hour day month weekday">Cron expression</FieldLabel>
              <input value={cron} onChange={(e) => setCron(e.target.value)}
                spellCheck={false}
                placeholder="*/15 * * * *"
                style={{
                  width: '100%',
                  background: C.input,
                  border: `1px solid ${cronValid ? C.borderStrong : C.error}`,
                  borderRadius: 8,
                  padding: '9px 12px',
                  color: C.fg1, fontSize: '.88rem',
                  fontFamily: "'JetBrains Mono', monospace",
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              {!cronValid && (
                <div style={{ color: C.error, fontSize: '.72rem', marginTop: 5 }}>
                  Doesn’t look like a valid cron expression.
                </div>
              )}
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  { expr: '*/15 * * * *', hint: 'Every 15 minutes' },
                  { expr: '0 * * * *',    hint: 'Top of every hour' },
                  { expr: '0 9 * * 1-5',  hint: 'Weekdays at 09:00' },
                  { expr: '0 0 1 * *',    hint: '1st of every month' },
                ].map(ex => (
                  <button key={ex.expr} onClick={() => setCron(ex.expr)}
                    title={ex.hint}
                    style={{
                      background: cron === ex.expr ? 'var(--info-bg)' : C.input,
                      border: `1px solid ${cron === ex.expr ? C.accentMuted : C.borderStrong}`,
                      color: cron === ex.expr ? C.accentBright : C.fg3,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '.72rem',
                      padding: '4px 8px',
                      borderRadius: 6, cursor: 'pointer',
                    }}
                  >{ex.expr}</button>
                ))}
              </div>
            </>
          )}

          {/* Live preview */}
          <div style={{
            marginTop: 18,
            background: 'var(--bg-input)',
            border: `1px solid ${C.accentMuted}`,
            borderRadius: 8,
            padding: '12px 14px',
          }}>
            <div style={{
              color: C.accentBright, fontSize: '.66rem',
              textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
              marginBottom: 4,
            }}>Schedule</div>
            <div style={{
              color: C.fg1, fontSize: '.88rem', fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
              marginBottom: 4,
            }}>{label}</div>
            <div style={{ color: C.fg5, fontSize: '.74rem' }}>{nextRunPreview()}</div>
          </div>

          {freq === 'manual' && (
            <Alert kind="info" style={{ marginTop: 12 }}>
              In <strong>Manual</strong> mode this pipeline only runs when you
              press <strong>Run pipeline</strong> or trigger it via the API.
              The schedule column will show “Manual”.
            </Alert>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 22px', borderTop: `1px solid ${C.border}`,
          background: C.bar,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            flex: 1, color: C.fg5, fontSize: '.72rem',
            fontFamily: "'JetBrains Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>was: <span style={{ color: C.fg3 }}>{pipeline.schedule || 'Manual'}</span></div>
          <Button kind="ghost"   size="sm" onClick={onClose}>Cancel</Button>
          <Button kind="primary" size="sm" disabled={!cronValid} onClick={submit}>
            Save schedule
          </Button>
        </div>
      </div>
    </div>
  );
}

// Best-effort parse of a schedule label back to form state. Unknown
// labels collapse to "Manual" — we never lose anything, since the parsed
// config is also stored alongside the label.
function parseSchedule(label) {
  const base = {
    freq: 'manual', time: '09:00', minute: 0,
    weekday: 'mon', day: 1, tz: 'UTC',
    cron: '*/15 * * * *',
  };
  if (!label || label === 'Manual') return base;
  const lower = label.toLowerCase();
  if (lower.startsWith('hourly')) {
    const m = label.match(/:(\d{2})/);
    return { ...base, freq: 'hourly', minute: m ? Number(m[1]) : 0 };
  }
  if (lower.startsWith('daily')) {
    const m = label.match(/(\d{2}:\d{2})\s+(\S+)/);
    return { ...base, freq: 'daily', time: m?.[1] || '09:00', tz: m?.[2] || 'UTC' };
  }
  if (lower.startsWith('weekly')) {
    const m = label.match(/\b(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\b\s+(\d{2}:\d{2})\s+(\S+)/i);
    return { ...base, freq: 'weekly', weekday: m?.[1]?.toLowerCase() || 'mon', time: m?.[2] || '09:00', tz: m?.[3] || 'UTC' };
  }
  if (lower.startsWith('monthly')) {
    const m = label.match(/day (\d+)\s+(\d{2}:\d{2})\s+(\S+)/);
    return { ...base, freq: 'monthly', day: m ? Number(m[1]) : 1, time: m?.[2] || '09:00', tz: m?.[3] || 'UTC' };
  }
  if (lower.startsWith('cron')) {
    const m = label.match(/cron\s+·\s+(.+)$/i);
    return { ...base, freq: 'cron', cron: m?.[1] || base.cron };
  }
  return base;
}

const schedNumStyle = {
  background: C.input,
  border: `1px solid ${C.borderStrong}`,
  borderRadius: 8,
  padding: '8px 11px',
  color: C.fg1, fontSize: '.86rem',
  fontFamily: "'JetBrains Mono', monospace",
  outline: 'none', boxSizing: 'border-box',
  width: 110,
};
